import { VFC, useEffect, useState, Dispatch, SetStateAction } from 'react'
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
  MouseSensor,
} from '@dnd-kit/core'
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable'
import { useRoutePlanning } from 'providers/RoutePlanningProvider'
import TagManager from 'react-gtm-module'
import { DeliveriesForPlace } from 'types/model/order'
import { CSS } from '@dnd-kit/utilities'
import { Box, Typography } from '@mui/material'
import { TRACKING_EVENT } from 'types/model/delivery'
import theme from 'lib/theme'
import { usePlaces } from 'providers/PlacesProvider'
import { OrderItem, DragHandleIcon, WarningTimeBox } from './elements'
import { outOfSlot } from './utils'
import { ListItem } from './ListItem'

const SortableItem: VFC<{ item: DeliveriesForPlace; displayIndex: number }> = ({
  item,
  displayIndex,
}) => {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id: item.parts[0].id })
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  }

  const shipment = Boolean(item.shipmentData[0])
    ? item.shipmentData[0].shipmentOrders
    : null

  const index = shipment
    ? shipment.findIndex(
        (shipment) => shipment.orderId === Number(item.parts[0].id)
      )
    : -1

  const finishAt =
    item.shipmentData &&
    item.shipmentData[0] &&
    item.shipmentData[0].shipmentRoute[index] !== null
      ? item.shipmentData[0].shipmentRoute[index]?.finishAt
      : null

  const endTime = index > -1 ? finishAt?.substring(11, 16) : null

  const deliveryOutOfSlot = outOfSlot(finishAt, item.orderDeliveryTimes)

  return (
    <OrderItem
      data-cy='draggable order'
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      style={style}
      display='flex'
      smallPadding
      alignItems='center'>
      <DragHandleIcon />
      <Box width={theme.spacing(7)} mr={3}>
        {deliveryOutOfSlot ? (
          <WarningTimeBox>
            <Typography variant='body2'>{endTime}</Typography>
          </WarningTimeBox>
        ) : (
          <Typography variant='body2' color={theme.palette.text.secondary}>
            {endTime}
          </Typography>
        )}
      </Box>
      <ListItem
        color={item.color}
        routeName={item.routeName}
        routeNameIndex={displayIndex}
        endTime={item.orderDeliveryTimes.endTime}
        startTime={item.orderDeliveryTimes.startTime}
        status={item.parts[0].status}
        partId={item.parts[0].id}
        deliveryId={item.parts[0].deliveryId}
        visibleId={item.parts[0].visibleId}
      />
    </OrderItem>
  )
}

const DraggableListItem = ({
  setShipmentsInOrder,
}: {
  setShipmentsInOrder: Dispatch<SetStateAction<DeliveriesForPlace[]>>
}) => {
  const { selectedPlace } = usePlaces()
  const { ordersToDisplay, toggleDeliveriesToDisplay } = useRoutePlanning()
  const [items, setItems] = useState<DeliveriesForPlace[]>([])

  useEffect(() => {
    if (ordersToDisplay && typeof toggleDeliveriesToDisplay === 'number') {
      setItems(ordersToDisplay as DeliveriesForPlace[])
    }
  }, [ordersToDisplay, toggleDeliveriesToDisplay])

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 25 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
    useSensor(MouseSensor)
  )

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event

    if (over && active.id !== over?.id) {
      setItems((items) => {
        const oldIndex = items.findIndex((i) => i.parts[0].id === active.id)
        const newIndex = items.findIndex((i) => i.parts[0].id === over.id)

        const newArray = arrayMove(items, oldIndex, newIndex)
        setShipmentsInOrder(newArray)
        return newArray
      })
      TagManager.dataLayer({
        dataLayer: {
          event: TRACKING_EVENT.REORDER_ROUTE,
          placeId: selectedPlace?.masterId || '',
          placeName: selectedPlace?.name || '',
          organizationId: selectedPlace?.organizationId || '',
        },
      })
    }
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}>
      <SortableContext
        items={items.map((i) => i.parts[0].id)}
        strategy={verticalListSortingStrategy}>
        {items.map((order) => {
          const displayIndex =
            items.findIndex((i) => i.parts[0].id === order.parts[0].id) + 1
          return (
            <SortableItem
              item={order}
              key={order.parts[0].id}
              displayIndex={displayIndex}
            />
          )
        })}
      </SortableContext>
    </DndContext>
  )
}

export default DraggableListItem
